import i18n from "@/i18n";
import { track as trackGa4 } from "@/analytics/ga4";
import { getStoredUTMParams } from "./utm-manager";

type AnalyticsPayload = Record<string, any>;

type TrackEventPayload = {
  event_category?: string;
  event_label?: string;
  section?: string;
  language?: string;
  value?: number;
  page?: string;
  [key: string]: any;
};

const getSafeUrl = (value?: string) => {
  if (!value) return undefined;

  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://rugira.ch";
    const url = new URL(value, base);
    return `${url.origin}${url.pathname}`;
  } catch {
    return undefined;
  }
};

const enrichPayload = (payload: AnalyticsPayload): AnalyticsPayload => {
  const enriched: AnalyticsPayload = { ...payload };

  if (!enriched.language) {
    enriched.language = i18n?.language ?? "en";
  }

  const utms = getStoredUTMParams();
  Object.entries(utms).forEach(([key, value]) => {
    if (value && enriched[key] === undefined) {
      enriched[key] = value;
    }
  });

  if (typeof window !== "undefined") {
    const safeLocation = getSafeUrl(window.location.href);
    const safeReferrer = getSafeUrl(document.referrer);

    if (safeLocation && enriched.page_location === undefined) {
      enriched.page_location = safeLocation;
    }

    if (safeReferrer && enriched.page_referrer === undefined) {
      enriched.page_referrer = safeReferrer;
    }
  }

  return enriched;
};

export const trackPageView = (url: string) => {
  if (typeof window === "undefined") return;

  trackGa4("page_view", {
    page_path: url,
    page_location: getSafeUrl(window.location.href),
    page_referrer: getSafeUrl(document.referrer),
    page_title: document.title,
  });
};

export function trackEvent(eventName: string, payload: TrackEventPayload): void;
export function trackEvent(
  eventName: string,
  category?: string,
  label?: string,
  value?: number,
  additionalParams?: Record<string, any>,
): void;
export function trackEvent(...args: any[]): void {
  let eventName: string;
  let payload: AnalyticsPayload;

  if (typeof args[1] === "object" && args[1] !== null && !Array.isArray(args[1])) {
    eventName = args[0];
    payload = enrichPayload(args[1] as AnalyticsPayload);
  } else {
    const [action, category, label, value, additionalParams] = args as [
      string,
      string | undefined,
      string | undefined,
      number | undefined,
      Record<string, any> | undefined,
    ];

    eventName = action;
    payload = enrichPayload({
      event_category: category ?? "interaction",
      event_label: label ?? "unknown",
      value,
      ...(additionalParams ?? {}),
    });
  }

  trackGa4(eventName, payload);
}

export const track = (eventName: string, params: AnalyticsPayload = {}) => {
  trackGa4(eventName, enrichPayload(params));
};

type LeadType = "early_access" | "waitlist" | "contact_sales";

export const trackStatusClick = (section: string) => {
  trackEvent("status_click", {
    event_category: "navigation",
    event_label: "status",
    section,
  });
};

export const trackChangelogClick = (section: string) => {
  trackEvent("changelog_click", {
    event_category: "navigation",
    event_label: "changelog",
    section,
  });
};

export const trackCalendlyBooked = (section: string, details?: Record<string, any>) => {
  const safeDetails: Record<string, any> = {};

  if (details?.event_type) {
    safeDetails.event_type = details.event_type;
  }

  trackEvent("preview_call_booked", {
    event_category: "conversion",
    event_label: "preview_call_requested",
    section,
    ...safeDetails,
  });
};

export const trackHeroCtaClick = (section: string) => {
  trackEvent("cta_click", {
    event_category: "cta",
    cta_id: "hero_request_early_access",
    event_label: "request_early_access",
    section,
  });
};

export const trackOnePagerDownload = (section: string, surface: string) => {
  trackEvent("file_download", {
    event_category: "asset",
    event_label: surface,
    section,
    file_name: "rugira_one_pager",
    file_extension: "pdf",
  });
};

const pillarLabelMap: Record<"risk" | "routing" | "audit", string> = {
  risk: "Risk",
  routing: "Routing",
  audit: "Audit",
};

export const trackPillarClick = (pillar: "risk" | "routing" | "audit") => {
  trackEvent("pillar_click", {
    event_category: "pillar",
    event_label: pillar,
    pillar_name: pillarLabelMap[pillar],
    section: "features",
  });
};

export const trackFormStep = (step: 1 | 2 | 3, section: string, label: string) => {
  trackEvent("form_step", {
    event_category: "form_step",
    event_label: label,
    step,
    section,
  });
};

export const trackFormComplete = (section: string, persona?: string, additional?: Record<string, any>) => {
  trackEvent("form_complete", {
    event_category: "form_completion",
    event_label: persona ?? "unknown",
    section,
    ...(additional ?? {}),
  });
};

export const trackVideoPlay = (videoId: string) => {
  trackEvent("video_play", {
    event_category: "engagement",
    event_label: videoId,
    section: "video",
  });
};

export const trackCarouselInteraction = (slideId: string, action: "view" | "navigate") => {
  trackEvent("carousel_interaction", {
    event_category: "carousel",
    event_label: slideId,
    section: "product_tour",
    interaction_type: action,
  });
};

export const trackGenerateLead = (payload: { lead_type: LeadType; section_id?: string; cta_id: string }) => {
  if (!payload.cta_id || !payload.lead_type) return;
  track("generate_lead", payload);
};
